import { getImpersonatedUserId, shouldImpersonatePath } from './impersonation'

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.dealcloserautopilot.com'

export type ApiResponse<T> = {
  readonly success: boolean
  readonly data?: T
  readonly error?: string
}

export type ApiRequestOptions = {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly body?: unknown
  readonly headers?: Record<string, string>
}

/**
 * The type of the authenticated fetch function returned by createApiFetcher / useApi.
 */
export type ApiFetch = <T>(path: string, options?: ApiRequestOptions) => Promise<ApiResponse<T>>

/**
 * Create an authenticated fetch function.
 * Call with a Clerk `getToken` function to get a pre-bound fetcher.
 */
export function createApiFetcher(getToken: () => Promise<string | null>): ApiFetch {
  return async function apiFetch<T>(
    path: string,
    options: ApiRequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const { method = 'GET', body, headers: extraHeaders } = options

    const token = await getToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...extraHeaders,
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Staff "view as user": attach the impersonation target on everything
    // except admin-console calls (which must keep the real staff identity).
    // The gateway ignores this header for non-staff callers.
    const impersonate = getImpersonatedUserId()
    if (impersonate && shouldImpersonatePath(path)) {
      headers['X-Impersonate-User-Id'] = impersonate
    }

    const url = `${API_BASE}${path}`

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        return {
          success: false,
          error:
            (errorBody as { error?: string }).error ||
            `Request failed with status ${response.status}`,
        }
      }

      const raw = (await response.json()) as unknown
      // Workers return { success: boolean, data: T, error?: string }.
      // Unwrap that envelope so callers always get { success, data: T }.
      if (
        typeof raw === 'object' &&
        raw !== null &&
        'success' in raw &&
        typeof (raw as { success: unknown }).success === 'boolean'
      ) {
        const envelope = raw as { success: boolean; data?: unknown; error?: string }
        if (!envelope.success) {
          return { success: false, error: envelope.error ?? 'Request failed' }
        }
        return { success: true, data: envelope.data as T }
      }
      // Plain (non-enveloped) response — return as-is
      return { success: true, data: raw as T }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Network error',
      }
    }
  }
}
